import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type LeaveAdjust,
  type LeaveBalanceItem,
  type LeaveBalanceResponse,
  type LeaveCalendarResponse,
  type LeaveImportResult,
  type LeaveRequestItem,
  type LeaveRequestListItem,
  type LeaveRequestListQuery,
  type LeaveRequestListResponse,
  type LeaveRequestStatus,
  type LeaveShorten,
  type MyLeaveResponse,
  type LeaveTypeCreate,
  type LeaveTypeItem,
  type LeaveTypeListResponse,
  type LeaveTypeUpdate,
  leaveBalanceItemSchema,
  leaveBalanceResponseSchema,
  leaveCalendarResponseSchema,
  leaveImportResultSchema,
  leaveRequestItemSchema,
  leaveRequestListItemSchema,
  leaveRequestListResponseSchema,
  leaveTypeItemSchema,
  leaveTypeListResponseSchema,
  myLeaveResponseSchema,
} from '@thrivehrm/shared';
import { API_BASE, api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/** The leave types catalog (FR-LEAVE-1, `leave.manage`), inactive ones included. */
export function useLeaveTypes(enabled = true) {
  return useQuery({
    enabled,
    queryKey: queryKeys.leave.types(true),
    queryFn: ({ signal }) =>
      api.get<LeaveTypeListResponse>('/leave/types', {
        query: { includeInactive: 'true' },
        schema: leaveTypeListResponseSchema,
        signal,
      }),
  });
}

/** Leave policy writes (`leave.manage`). Errors are `ApiError`s (409 code taken, 422 rules). */
export const leaveApi = {
  createType: (input: LeaveTypeCreate) =>
    api.post<LeaveTypeItem>('/leave/types', input, { schema: leaveTypeItemSchema }),
  updateType: (id: string, patch: LeaveTypeUpdate) =>
    api.patch<LeaveTypeItem>(`/leave/types/${id}`, patch, { schema: leaveTypeItemSchema }),
  /** Checks (`dryRun`) or applies an opening-balance workbook (FR-LEAVE-3). */
  importOpening: (file: File, dryRun: boolean) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<LeaveImportResult>('/leave/balances/import', form, {
      query: { dryRun: String(dryRun) },
      schema: leaveImportResultSchema,
    });
  },
};

/** Where the opening-balance template downloads from. */
export const OPENING_TEMPLATE_URL = `${API_BASE}/leave/balances/import/template.xlsx`;

/** A leave policy write that refreshes the catalog once it succeeds. */
export function useLeaveTypeMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.leave.all }),
  });
}

// ── Requests and balances (FR-LEAVE-3…8, T-074) ─────────────────────────────────────────────

/** `GET /me/leave` — my leave year: types with their rules and figures. */
export function useMyLeave(year: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.leave.mine(year),
    queryFn: ({ signal }) =>
      api.get<MyLeaveResponse>('/me/leave', {
        query: { year: String(year) },
        schema: myLeaveResponseSchema,
        signal,
      }),
    enabled,
  });
}

export type LeaveRequestParams = Partial<Omit<LeaveRequestListQuery, 'status'>> &
  Pick<LeaveRequestListQuery, 'page' | 'pageSize' | 'scope'> & {
    status?: readonly LeaveRequestStatus[];
  };

/** `GET /leave/requests` by scope (me / team / all); keeps the previous page while paging. */
export function useLeaveRequests(params: LeaveRequestParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.leave.requests(params),
    queryFn: ({ signal }) => {
      const query: Record<string, string | readonly string[]> = {};
      for (const [key, value] of Object.entries(params) as [string, unknown][]) {
        if (typeof value === 'number') query[key] = String(value);
        else if (typeof value === 'string' && value !== '') query[key] = value;
        else if (Array.isArray(value) && value.length > 0) query[key] = value.map(String);
      }
      return api.get<LeaveRequestListResponse>('/leave/requests', {
        query,
        schema: leaveRequestListResponseSchema,
        signal,
      });
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** `GET /leave/calendar` — pending and approved leave in a range (≤ 62 days). */
export function useLeaveCalendar(params: { scope: 'team' | 'all'; from: string; to: string }) {
  return useQuery({
    queryKey: queryKeys.leave.calendar(params),
    queryFn: ({ signal }) =>
      api.get<LeaveCalendarResponse>('/leave/calendar', {
        query: params,
        schema: leaveCalendarResponseSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/** `GET /leave/balances` — an employee's balances and ledger (leave.read.all). */
export function useLeaveBalances(employeeId: string | null, year: number) {
  return useQuery({
    queryKey: queryKeys.leave.balances(employeeId ?? '', year),
    queryFn: ({ signal }) =>
      api.get<LeaveBalanceResponse>('/leave/balances', {
        query: { employeeId: employeeId ?? '', year: String(year) },
        schema: leaveBalanceResponseSchema,
        signal,
      }),
    enabled: employeeId !== null,
  });
}

/** The leave request behind a LEAVE approval (the approvals inbox). */
export function useLeaveByApproval(approvalId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.leave.byApproval(approvalId),
    queryFn: ({ signal }) =>
      api.get<LeaveRequestListItem>(`/leave/requests/approval/${approvalId}`, {
        schema: leaveRequestListItemSchema,
        signal,
      }),
    enabled,
  });
}

/** What a new request sends; `file` is the medical document (multipart). */
export interface LeaveRequestInput {
  employeeId?: string;
  leaveTypeId: string;
  fromDate: string;
  toDate: string;
  startHalf?: 'SECOND_HALF';
  endHalf?: 'FIRST_HALF';
  reason?: string;
  file?: File;
}

/** Leave request writes (FR-LEAVE-4…7). Errors are `ApiError`s (422 with the field). */
export const leaveRequestsApi = {
  create: ({ file, ...fields }: LeaveRequestInput) => {
    if (!file)
      return api.post<LeaveRequestItem>('/leave/requests', fields, {
        schema: leaveRequestItemSchema,
      });
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      if (typeof value === 'string') form.append(key, value);
    }
    form.append('file', file);
    return api.post<LeaveRequestItem>('/leave/requests', form, { schema: leaveRequestItemSchema });
  },
  cancel: (id: string, reason?: string) =>
    api.post<LeaveRequestItem>(`/leave/requests/${id}/cancel`, reason ? { reason } : {}, {
      schema: leaveRequestItemSchema,
    }),
  shorten: (id: string, body: LeaveShorten) =>
    api.post<LeaveRequestItem>(`/leave/requests/${id}/shorten`, body, {
      schema: leaveRequestItemSchema,
    }),
  adjust: (body: LeaveAdjust) =>
    api.post<LeaveBalanceItem>('/leave/balances/adjust', body, { schema: leaveBalanceItemSchema }),
};

/** A leave write that refreshes every leave view (and the approvals inbox) once it succeeds. */
export function useLeaveMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.leave.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.approvals.all }),
      ]),
  });
}

/** Where a request's medical document opens (AC-10: the API decides who may). */
export const leaveDocumentUrl = (requestId: string, documentId: string) =>
  `${API_BASE}/leave/requests/${requestId}/documents/${documentId}/file`;

/** The leave balances report (leave.read.all). */
export const leaveBalancesExportUrl = (year: number) =>
  `${API_BASE}/exports/leave-balances.xlsx?year=${String(year)}`;
